import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminUserService } from "@/services/api/admin.user.service";
import { useNotification } from "@/contexts/notification/NotificationContext";
import {
  UserPlus,
  Users,
  UserCheck,
  UserX,
  ChevronRight,
  Activity,
  FileText,
  Package,
  Briefcase,
  FileCheck,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

// Notification Badge Component
const NotificationBadge = ({ count, priority = "low", size = "sm" }) => {
  if (!count || count === 0) return null;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "bg-red-500 text-white animate-pulse";
      case "high":
        return "bg-orange-500 text-white";
      case "medium":
        return "bg-yellow-500 text-white";
      case "low":
      default:
        return "bg-blue-500 text-white";
    }
  };

  const getSizeClasses = (size) => {
    switch (size) {
      case "xs":
        return "text-xs px-1.5 py-0.5 h-4 min-w-4";
      case "sm":
        return "text-xs px-2 py-0.5 h-5 min-w-5";
      case "md":
        return "text-sm px-2.5 py-1 h-6 min-w-6";
      default:
        return "text-xs px-2 py-0.5 h-5 min-w-5";
    }
  };

  return (
    <span
      className={`
      inline-flex items-center justify-center rounded-full font-medium
      ${getPriorityColor(priority)}
      ${getSizeClasses(size)}
    `}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};

// Red Dot Indicator Component
const RedDotIndicator = ({ show }) => {
  if (!show) return null;

  return (
    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse border-2 border-white shadow-lg"></div>
  );
};

// Real API call with authentication
const fetchDashboardData = async () => {
  try {
    const token = localStorage.getItem("authToken");
    const data = await adminUserService.getDashboardStats(token);
    return data;
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    throw error;
  }
};

export default function Alluser() {
  const navigate = useNavigate();
  const {
    unreadCount,
    categorizedCounts,
    detailedUnreadCount,
    getCategoryCount,
    markCategoryAsRead,
    refreshNotifications, // Add this if available in your context
  } = useNotification();

  // Replace your existing handleCardClick function with this updated version:

  // Replace your existing handleCardClick function with this updated version:

  const handleCardClick = async () => {
    try {
      // Get the auth token
      const token = localStorage.getItem("authToken");

      // First check if there are unread User notifications
      const userUnreadCount = getCategoryCount("User", true);
  

      if (userUnreadCount > 0) {
     
        
        // Pass the token to markCategoryAsRead if it requires authentication
        await markCategoryAsRead("User", token);
    
        

        // Force refresh notification context
        if (refreshNotifications) {
 
          
          await refreshNotifications();
        }

        // Add a small delay to ensure state updates
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Navigate after marking as read
      navigate("/admindashboard/user-details");
    } catch (error) {
      console.error("Failed to mark User notifications as read:", error);
      // Navigate anyway even if marking as read fails
      navigate("/admindashboard/user-details");
    }
  };

  const handlekycStatus = async () => {
    try {
      // Get the auth token
      const token = localStorage.getItem("authToken");

      // First check if there are unread KYC notifications
      const kycUnreadCount = getCategoryCount("Kyc", true);


      if (kycUnreadCount > 0) {
  
        // Pass the token to markCategoryAsRead if it requires authentication
        await markCategoryAsRead("Kyc", token);
   

        // Force refresh notification context
        if (refreshNotifications) {
    
          await refreshNotifications();
        }

        // Add a small delay to ensure state updates
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Navigate after marking as read
      navigate("/admindashboard/Kyc-Status");
    } catch (error) {
      console.error("Failed to mark KYC notifications as read:", error);
      // Navigate anyway even if marking as read fails
      navigate("/admindashboard/Kyc-Status");
    }
  };

  const handlePortfolio = async () => {
    try {
      // Get the auth token
      const token = localStorage.getItem("authToken");

      // First check if there are unread Portfolio notifications
      const portfolioUnreadCount = getCategoryCount("Portfolio", true);
    

      if (portfolioUnreadCount > 0) {
     
        // Pass the token to markCategoryAsRead if it requires authentication
        await markCategoryAsRead("Portfolio", token);
 

        // Force refresh notification context
        if (refreshNotifications) {
      
          await refreshNotifications();
        }

        // Add a small delay to ensure state updates
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Navigate after marking as read
      navigate("/admindashboard/user-portfolios");
    } catch (error) {
      console.error("Failed to mark Portfolio notifications as read:", error);
      // Navigate anyway even if marking as read fails
      navigate("/admindashboard/user-portfolios");
    }
  };

  const handleadminInfo = () => {
    navigate("/admindashboard/admin-profile");
  };

  const handleblog = () => {
    navigate("/admindashboard/blog-stats");
  };

  const handleaproductlist = () => {
    navigate("/admindashboard/product-list");
  };

  // Removed duplicate handleportfolio function since it's same as handlePortfolio

  const [dashboardData, setDashboardData] = useState({
    users: {
      total: 0,
      active: 0,
      recent: 0,
    },
    kyc: {
      pending: 0,
      approved: 0,
      rejected: 0,
      verificationRate: 0,
      approvalRate: 0,
    },
    admins: {
      total: 0,
    },
    portfolios: {
      total: 0,
      active: 0,
      inactive: 0,
      closed: 0,
      recent: 0,
      distribution: {
        active: 0,
        inactive: 0,
        closed: 0,
      },
      values: {
        totalValue: 0,
        avgValue: 0,
        maxValue: 0,
      },
    },
    blogs: {
      total: 0,
    },
    products: {
      total: 0,
      recent: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get notification counts for different categories with better error handling
  const getUserNotificationCount = () => {
    try {
      return categorizedCounts?.categories?.User?.unread || 0;
    } catch (error) {
      console.error("Error getting user notification count:", error);
      return 0;
    }
  };

  const getKycNotificationCount = () => {
    try {
      return categorizedCounts?.categories?.Kyc?.unread || 0;
    } catch (error) {
      console.error("Error getting KYC notification count:", error);
      return 0;
    }
  };

  const getPortfolioNotificationCount = () => {
    try {
      return categorizedCounts?.categories?.Portfolio?.unread || 0;
    } catch (error) {
      console.error("Error getting Portfolio notification count:", error);
      return 0;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchDashboardData();
        if (response.status === "success") {
          setDashboardData(response.data);
        } else {
          setError("Failed to load dashboard data");
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        setError("Error connecting to the server");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <Activity className="w-12 h-12 text-blue-500 animate-pulse" />
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-500 mb-2">
            <Activity className="w-12 h-12 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-red-700 mb-2">{error}</h2>
          <p className="text-gray-600">
            Please check your connection or try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* User Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div
          className="bg-white rounded-xl p-4 shadow-md border border-gray-100 transition-all hover:shadow-lg relative cursor-pointer"
          onClick={handleCardClick}
        >
          <RedDotIndicator show={getUserNotificationCount() > 0} />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {dashboardData.users?.total || 0}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full relative">
              <Users className="h-5 w-5 text-blue-600" />
              {getUserNotificationCount() > 0 && (
                <div className="absolute -top-1 -right-1">
                  <NotificationBadge
                    count={getUserNotificationCount()}
                    priority="urgent"
                    size="xs"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="bg-white rounded-xl p-4 shadow-md border border-gray-100 transition-all hover:shadow-lg relative cursor-pointer"
          onClick={handleCardClick}
        >
          {/* <RedDotIndicator show={getUserNotificationCount() > 0} /> */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Active Users</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">
                {dashboardData.users?.active || 0}
              </p>
            </div>
            <div className="bg-emerald-100 p-3 rounded-full">
              <UserCheck className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
        </div>

        <div
          className="bg-white rounded-xl p-4 shadow-md border border-gray-100 transition-all hover:shadow-lg relative cursor-pointer"
          onClick={handleCardClick}
        >
          {/* <RedDotIndicator show={getUserNotificationCount() > 0} /> */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Inactive Users
              </p>
              <p className="text-2xl font-bold text-orange-500 mt-1">
                {dashboardData.users?.total - dashboardData.users?.active}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <UserX className="h-5 w-5 text-orange-500" />
            </div>
          </div>
        </div>

        <div
          className="bg-white rounded-xl p-4 shadow-md border border-gray-100 transition-all hover:shadow-lg relative cursor-pointer"
          onClick={handleCardClick}
        >
          {/* <RedDotIndicator show={getUserNotificationCount() > 0} /> */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">New Users</p>
              <p className="text-2xl font-bold text-indigo-600 mt-1">
                {dashboardData?.users?.recent}
              </p>
            </div>
            <div className="bg-indigo-100 p-3 rounded-full">
              <UserPlus className="h-5 w-5 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* KYC and Portfolio Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* KYC Stats */}
        <div
          className="bg-white rounded-xl p-5 shadow-md border border-gray-100 relative cursor-pointer"
          onClick={handlekycStatus}
        >
          <RedDotIndicator show={getKycNotificationCount() > 0} />
          <div className="flex items-center mb-4">
            <FileCheck className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">
              KYC Verification
            </h3>
            {getKycNotificationCount() > 0 && (
              <div className="ml-auto">
                <NotificationBadge
                  count={getKycNotificationCount()}
                  priority="high"
                  size="sm"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-yellow-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-500">Pending</p>
              <p className="text-xl font-bold text-yellow-600">
                {dashboardData?.kyc.pending}
              </p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-500">Approved</p>
              <p className="text-xl font-bold text-green-600">
                {dashboardData?.kyc?.approved}
              </p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-500">Rejected</p>
              <p className="text-xl font-bold text-red-600">
                {dashboardData.kyc.rejected}
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Verification Rate</p>
              <div className="flex items-center">
                <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${dashboardData.kyc.verificationRate}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold">
                  {dashboardData.kyc.verificationRate}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Approval Rate</p>
              <div className="flex items-center">
                <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                  <div
                    className="bg-green-600 h-2.5 rounded-full"
                    style={{ width: `${dashboardData.kyc.approvalRate}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold">
                  {dashboardData.kyc.approvalRate}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio Stats */}
        <div
          className="bg-white rounded-xl p-5 shadow-md border border-gray-100 relative cursor-pointer"
          onClick={handlePortfolio}
        >
          <RedDotIndicator show={getPortfolioNotificationCount() > 0} />
          <div className="flex items-center mb-4">
            <Briefcase className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">
              Portfolio Statistics
            </h3>
            {getPortfolioNotificationCount() > 0 && (
              <div className="ml-auto">
                <NotificationBadge
                  count={getPortfolioNotificationCount()}
                  priority="medium"
                  size="sm"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-500">Total</p>
              <p className="text-xl font-bold text-blue-600">
                {dashboardData.portfolios.total}
              </p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-500">Active</p>
              <p className="text-xl font-bold text-green-600">
                {dashboardData.portfolios.active}
              </p>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-500">Inactive</p>
              <p className="text-xl font-bold text-orange-600">
                {dashboardData.portfolios.inactive}
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-lg font-semibold">
                Rs {dashboardData.portfolios.values.totalValue.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Average Value</p>
              <p className="text-lg font-semibold">
                Rs{" "}
                {Math.round(
                  dashboardData.portfolios.values.avgValue
                ).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="mt-3">
            <p className="text-sm text-gray-500">Distribution</p>
            <div className="flex h-2 mt-1 mb-2">
              <div
                className="bg-green-500 rounded-l"
                style={{
                  width: `${dashboardData.portfolios.distribution.active}%`,
                }}
              ></div>
              <div
                className="bg-orange-400"
                style={{
                  width: `${dashboardData.portfolios.distribution.inactive}%`,
                }}
              ></div>
              <div
                className="bg-red-400 rounded-r"
                style={{
                  width: `${dashboardData.portfolios.distribution.closed}%`,
                }}
              ></div>
            </div>
            <div className="flex text-xs justify-between">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                <span>
                  Active {dashboardData.portfolios.distribution.active}%
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-orange-400 rounded-full mr-1"></div>
                <span>
                  Inactive {dashboardData.portfolios.distribution.inactive}%
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-1"></div>
                <span>
                  Closed {dashboardData.portfolios.distribution.closed}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Admin Stats */}
        <div
          className="bg-white rounded-xl p-5 shadow-md border border-gray-100 cursor-pointer"
          onClick={handleadminInfo}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Users className="h-5 w-5 text-purple-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">
                Admin Stats
              </h3>
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-purple-50 rounded-lg">
            <p className="text-gray-600">Total Admins</p>
            <p className="text-xl font-bold text-purple-600">
              {dashboardData.admins.total}
            </p>
          </div>
        </div>

        {/* Blogs Stats */}
        <div
          className="bg-white rounded-xl p-5 shadow-md border border-gray-100 cursor-pointer"
          onClick={handleblog}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-indigo-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">
                Blog Stats
              </h3>
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-50 rounded-lg">
            <p className="text-gray-600">Total Blog Posts</p>
            <p className="text-xl font-bold text-indigo-600">
              {dashboardData.blogs.total}
            </p>
          </div>
        </div>

        {/* Products Stats */}
        <div
          className="bg-white rounded-xl p-5 shadow-md border border-gray-100 cursor-pointer"
          onClick={handleaproductlist}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Package className="h-5 w-5 text-teal-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">
                Product Stats
              </h3>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between px-3 py-2 bg-teal-50 rounded-lg">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-lg font-bold text-teal-600">
                {dashboardData.products.total}
              </p>
            </div>
            <div className="flex items-center justify-between px-3 py-2 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">Recent</p>
              <p className="text-lg font-bold text-yellow-600">
                {dashboardData.products.recent}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Value Details Card */}
      <div
        className="bg-white rounded-xl p-5 shadow-md border border-gray-100 mb-6 relative cursor-pointer"
        onClick={handlePortfolio}
      >
        <RedDotIndicator show={getPortfolioNotificationCount() > 0} />
        <div className="flex items-center mb-4">
          <Briefcase className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">
            Portfolio Value Details
          </h3>
          {getPortfolioNotificationCount() > 0 && (
            <div className="ml-auto">
              <NotificationBadge
                count={getPortfolioNotificationCount()}
                priority="medium"
                size="sm"
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Value</p>
            <p className="text-xl font-bold text-blue-700">
              Rs {dashboardData.portfolios.values.totalValue.toLocaleString()}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Average Value</p>
            <p className="text-xl font-bold text-green-700">
              Rs{" "}
              {Math.round(
                dashboardData.portfolios.values.avgValue
              ).toLocaleString()}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Maximum Value</p>
            <p className="text-xl font-bold text-purple-700">
              Rs {dashboardData.portfolios.values.maxValue.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
